import { RichTextEditor } from './rich-text-editor';

import type { SharedEditorToolbar } from './use-shared-editor-toolbar';

/**
 * The anchored text-styling bar: buttons that stay put while the content below them scrolls,
 * with a rule separating the two.
 *
 * Lifted out of `InputBlockFrame`, which had it inline, so a DISPLAY block can have the same bar
 * instead of a copy of its class string. The eleven input blocks reach it through the frame; a
 * display block has no frame and renders it directly.
 *
 * It is a headless `RichTextEditor` — `hideContent` plus `targetEditor` — which is how one
 * toolbar drives whichever field is focused. That matters even for a block with a single field:
 * the alternative is an editor rendering its own toolbar inline, and then nothing can sit
 * between the buttons and the text.
 *
 * The caller owns STICKINESS. The frame wraps this in its own `sticky top-0 z-20` box shared
 * with the feedback bar, because the toolbar's height changes with focus and any offset a second
 * bar hard-coded would be wrong half the time. A block whose content scrolls internally needs no
 * sticky at all — the bar is simply a sibling above the scroll.
 */
export function AnchoredStylingBar({ toolbar, fullScreen, className = '', vertical = false }: {
  toolbar: SharedEditorToolbar;
  /** Phone full-screen, which changes how the colour and symbol popups are presented. */
  fullScreen?: boolean;
  /** Padding differs by host: the frame uses its own `px-6`, a block its editor's. */
  className?: string;
  /**
   * A COLUMN DOWN THE LEFT instead of a band across the top — the phone-landscape shape.
   *
   * `InputBlockFrame` reached this first and for the same reason: a landscape phone is 390px
   * tall, and two horizontal rows of buttons spend a quarter of that before any writing. The
   * same headless toolbar either way, stacked rather than wrapped, so nothing about which
   * editor it drives changes with the orientation.
   */
  vertical?: boolean;
}) {
  if (vertical) {
    return (
      <div className={`w-24 shrink-0 overflow-y-auto border-r border-ws-border bg-ws-surface p-1.5 ${className}`}>
        <RichTextEditor
          content=''
          onChange={() => {}}
          hideContent
          targetEditor={toolbar.activeEditor}
          toolbarVisible={!!toolbar.activeEditor}
          toolbarLayout='vertical'
        />
      </div>
    );
  }
  return (
    <div className={`border-b border-ws-border bg-ws-surface ${className || 'px-6 py-3'}`}>
      <RichTextEditor
        content=''
        onChange={() => {}}
        hideContent
        targetEditor={toolbar.activeEditor}
        toolbarVisible={!!toolbar.activeEditor}
        phoneFullScreen={fullScreen}
      />
    </div>
  );
}
